import React, { useState, useImperativeHandle, useEffect, forwardRef, useTransition, Suspense } from "react";
import DtvModal from "@directv-digital/dds.ui.dtv-modal";

const PkgComparisonModalV2 = React.lazy(() => import("./pkg-comparison-modal-v2").then(m => ({ default: m.PkgComparisonModalV2 })));
const DtvExternalServiceComparisonChart = React.lazy(() => import("@directv-digital/dds.ui.dtv-external-service-comparison-chart"));
import { IDtvTextProps } from "@directv-digital/dds.ui.dtv-text";

export interface IPkgCompareModal {
  id: string;
  ref: any;
  modalFeed: any;// IExternalServiceComparisonChartProps | IPackageComparisonModalV2Props;
  modalFeedNoLocals?: any;// IExternalServiceComparisonChartProps | IPackageComparisonModalV2Props;
  toggledNoLocals: boolean;
  isCardSetSatellite: boolean;
  packagePrices?: any;
  packagePriceCopy?: any;
  chnlCountsByPackage?: any;
}

export interface IBasePkgComparisonModalProps {
  id: string;
  variant?: string;
  toolTitle?: IDtvTextProps;
  backgroundColor?: any[];
  columns: any[];
  rows: any[];
  sourceHasNoLocals?: boolean;
  sourceChannelCounts?: string[];
  sourcePrices?: string[];
  isStreamPackages?: boolean;
}

export const PkgComparisonModalV2Module = forwardRef((props: IPkgCompareModal, ref) => {
  const {
    modalFeed,
    modalFeedNoLocals,
    isCardSetSatellite,
    toggledNoLocals,
    packagePrices,
    packagePriceCopy,
    chnlCountsByPackage
  } = props;

  const chooseFeed = () => (toggledNoLocals && modalFeedNoLocals !== undefined ?
    modalFeedNoLocals :
    modalFeed);

  const [openPkgComparisonModal, setOpenPkgComparisonModal] = useState(false);
  // false until the first open request — the modal subtree (DtvModal + lazy
  // comparison chart) stays unmounted for users who never open it
  const [hasOpened, setHasOpened] = useState(false);
  const [feed, setFeed] = useState(chooseFeed());
  const [_isPending, startTransition] = useTransition();

  useEffect(() => {
    startTransition(() => setFeed(chooseFeed()));
  }, [toggledNoLocals, isCardSetSatellite, packagePrices, packagePriceCopy, chnlCountsByPackage]);

  const updateOpenPkgCompareModal = sourceIx => {
    if (!feed) {
      return false;
    }
    startTransition(() => {
      const dynamicFeed = {
        ...feed,
        sourceIndex: feed?.enableSourcePkgHighlighting && sourceIx >= 0 ? sourceIx : -99,
        sourcePrices: Array.isArray(packagePrices) && packagePrices?.every(p => p !== null) ? packagePrices : null,
        sourceChannelCounts: Array.isArray(chnlCountsByPackage) && chnlCountsByPackage.every(ct => ct !== null) ?
          chnlCountsByPackage : null,
        sourceHasNoLocals: toggledNoLocals === true,
        isStreamPackages: !isCardSetSatellite
      };
      setFeed(dynamicFeed);
      setHasOpened(true);
      setOpenPkgComparisonModal(true);
    });
    return true;
  };

  useImperativeHandle(ref, () => ({ updateOpenPkgCompareModal }));

  if (!hasOpened) {
    return null;
  }

  const modalBody = feed?.variant === "comparison-tool-v2" ? (
    <Suspense fallback={<div style={{
      padding: '2rem',
      textAlign: 'center'
    }}>Loading...</div>}>
      <PkgComparisonModalV2 {...feed} />
    </Suspense>
  ) : (
    <Suspense fallback={<div style={{
      padding: '2rem',
      textAlign: 'center'
    }}>Loading...</div>}>
      <DtvExternalServiceComparisonChart {...feed} removeSpaceAround />
    </Suspense>
  );

  return (
    <DtvModal
      handleClose={() => setOpenPkgComparisonModal(false)}
      hideScrollbar={feed?.variant === "comparison-tool-v2"}
      open={openPkgComparisonModal}
      closeIconStyle={{
        ...(feed?.variant === "comparison-tool-v2" && {
          width: {
 xs: "20px",
md: "24px"
},
          height: {
 xs: "20px",
md: "24px"
},
          marginBottom: "-4px"
        })
      }}
      modalBodyStyle={{
        ...(feed?.variant === "comparison-tool-v2" ? {
            position: 'relative',
            top: { // = toolTitle lineHeight + toolTitle pseudo margin bottom
              xs: "45px", // 29 + 16
              sm: "56px", // 36 + 20
              md: "63px" // 43 + 20
            },
            marginBottom: { // offset top
              xs: "-45px",
              sm: "-56px",
              md: "-63px"
            },
            maxHeight: { // = modal - (toolTitle + Mb) - close icon space height - table Pb
              xs: "calc(100% - 45px - 40px)",
              sm: "calc(100% - 56px - 48px - 24px)",
              md: "calc(100% - 63px - 56px - 32px)"
            },
            ml: "0px !important",
            mr: "0px !important",
            mb: "0px !important",
            left: {
              xs: 0,
              sm: "32px",
              md: "54px"
            },
            width: {
              xs: "calc(100% - 1px)",
              sm: "calc(100% - 64px)",
              md: "calc(100% - 108px)"
            }
        } : {
            position: 'relative',
            top: {
              xs: "48px", // offset grid to bottom of title height (28px) + margin (20px)
              md: "40px" // offset grid to bottom of title height (20px) + margin (20px)
            },
            marginBottom: { // offset 'top'
              xs: "-48px",
              md: "-40px"
            },
            maxHeight: {
              xs: "calc(100% - 44px)", // full height - 22px (pb) - 22px (real mB)
              sm: "calc(100% - 78px)", // full height - 36px (pB) - 32px (real mB)
              md: "calc(100% - 92px)" // full height - 54px (mT) - 38px (real mB)
            },

            marginLeft: {
              xs: "20px",
              sm: "32px",
              md: "48px"
            },
            marginRight: { // equal to close icon mR
              xs: "12px"
            }
        })
      }}
      modalStyle={{
        ...(feed?.variant === "comparison-tool-v2" ? {
            borderRadius: {
              xs: "12px 12px 0 0",
              sm: "12px"
            },
            height: {
              xs: '100%'
            },
            // maintains a 1:1.25 aspect ratio
            maxWidth: {
              xs: '100vw',
              sm: "900px",
              md: "1000px",
              lg: "1200px"
            },
            maxHeight: {
              xs: 'calc(100vh - 50px)',
              sm: '720px',
              md: "800px",
              lg: "960px"
            },
            // override closeIcon container
            "& > div:first-child": {
              marginBottom: {
                xs: "8px",
                sm: "12px"
              },
              marginTop: {
                xs: "12px",
                sm: "16px",
                md: "20px"
              },
              marginRight: {
                xs: "12px",
                sm: "16px",
                md: "20px"
              }
            }
        } : {
            borderRadius: {
              xs: "10px 10px 0 0",
              sm: "10px"
            },
            maxWidth: {
              sm: '750px',
              md: "900px",
              lg: "1000px"
            },
            maxHeight: {
              xs: 'calc(100% - 16px)',
              sm: 'calc(100% - 104px)' // 52px mT/mB
            },
            height: {
              xs: '100%'
            },
            paddingBottom: {
              xs: "52px", // 34px (reset modal body height) + 26px (pB)
              sm: "36px",
              md: "48px"
            }
        })
      }}
      >
      {modalBody}
    </DtvModal>
  );
});
